/**
 * SentView — "Posta inviata": tutte le mail spedite DALL'APPLICAZIONE
 * (nuove mail e risposte). Fonte: webmail.sent_messages, alimentata al
 * momento dell'invio (POST /messages/send).
 *
 * Grafica allineata agli altri elenchi (wm-table). Click sulla riga:
 * espande il dettaglio della mail inviata (testo/HTML) sotto la riga.
 */
import { useMemo, useState } from 'react';
import { Send, Loader2, ArrowUp, ArrowDown, Reply, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { messagesApi } from '../../lib/api';
import { HelpHint } from '../ui/HelpHint';

export interface SentMail {
  id: string;
  account_id: string;
  message_id: string | null;
  in_reply_to: string | null;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  sent_at: string;
}

type SortKey = 'sent_at' | 'to' | 'subject';

/** Sanitizer minimale: rimuove script/iframe dall'HTML che abbiamo inviato noi. */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
}

export function SentView() {
  const { data, isLoading } = useQuery({
    queryKey: ['sent-messages'],
    queryFn: () => messagesApi.sentList() as Promise<{ sent: SentMail[] }>,
    refetchInterval: 60_000,
  });
  const items = data?.sent ?? [];

  const [openId, setOpenId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('sent_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'sent_at' ? 'desc' : 'asc');
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items;
    if (q)
      arr = arr.filter(
        (m) =>
          (m.subject ?? '').toLowerCase().includes(q) ||
          m.to_addresses.join(' ').toLowerCase().includes(q),
      );
    const sorted = [...arr];
    sorted.sort((a, b) => {
      const cmp =
        sortKey === 'sent_at'
          ? new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
          : sortKey === 'to'
            ? a.to_addresses.join(',').localeCompare(b.to_addresses.join(','), 'it', { sensitivity: 'base' })
            : (a.subject ?? '').localeCompare(b.subject ?? '', 'it', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [items, search, sortKey, sortDir]);

  function SortTh({ label, k, width }: { label: string; k: SortKey; width?: number }) {
    const active = sortKey === k;
    return (
      <th style={width ? { width } : undefined}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className="inline-flex items-center gap-1 text-inherit"
          title={`Ordina per ${label}`}
        >
          {label}
          {active && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        </button>
      </th>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="wm-list flex flex-1 flex-col overflow-hidden">
        <div className="shrink-0 px-6 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1
              className="flex items-center gap-2 text-xl font-semibold"
              style={{ color: 'var(--text)' }}
            >
              <Send size={18} style={{ color: 'var(--accent)' }} /> Posta inviata
              {/* Aiuto contestuale della sezione */}
              <HelpHint topic="sent" />
              <span className="text-sm font-normal" style={{ color: 'var(--muted)' }}>
                · {items.length} mail inviate dall'applicazione
              </span>
            </h1>
            <input
              className="wm-tagadd-input"
              style={{ width: 260, position: 'static', transform: 'none' }}
              placeholder="Cerca: destinatario, oggetto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="relative flex-1 overflow-auto px-5 pb-4 pt-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={18} className="animate-spin" style={{ color: 'var(--muted)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm" style={{ color: 'var(--muted)' }}>
              Nessuna mail inviata dall'applicazione{search ? ' che corrisponda alla ricerca' : ''}.
            </div>
          ) : (
            <table
              className="wm-table border-separate"
              style={{ borderSpacing: 0, tableLayout: 'fixed', width: '100%' }}
            >
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <SortTh label="Inviata il" k="sent_at" width={130} />
                  <SortTh label="A" k="to" width={260} />
                  <SortTh label="Oggetto" k="subject" />
                  <th style={{ width: 110 }}>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const open = openId === m.id;
                  return (
                    <SentRowGroup
                      key={m.id}
                      mail={m}
                      open={open}
                      onToggle={() => setOpenId(open ? null : m.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SentRowGroup({
  mail,
  open,
  onToggle,
}: {
  mail: SentMail;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={'wm-row' + (open ? ' is-active' : '')} onClick={onToggle}>
        <td style={{ color: 'var(--muted)' }}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </td>
        <td className="whitespace-nowrap" style={{ color: 'var(--muted)' }}>
          {format(new Date(mail.sent_at), 'dd MMM HH:mm', { locale: it })}
        </td>
        <td>
          <span className="truncate" title={mail.to_addresses.join(', ')}>
            {mail.to_addresses.join(', ')}
          </span>
        </td>
        <td>
          <span className="truncate">{mail.subject ?? '(senza oggetto)'}</span>
        </td>
        <td>
          {mail.in_reply_to ? (
            <span className="wm-badge wm-badge-cat">
              <Reply size={10} /> Risposta
            </span>
          ) : (
            <span className="wm-badge wm-badge-cat">Nuova</span>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--surface-low)', padding: '12px 16px' }}>
            {mail.cc_addresses.length > 0 && (
              <div className="mb-2 text-xs" style={{ color: 'var(--muted)' }}>
                Cc: {mail.cc_addresses.join(', ')}
              </div>
            )}
            {mail.body_html ? (
              <div
                className="prose prose-sm max-w-none text-sm"
                style={{ color: 'var(--text)' }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(mail.body_html) }}
              />
            ) : (
              <pre
                className="whitespace-pre-wrap font-sans text-sm leading-relaxed"
                style={{ color: 'var(--text)' }}
              >
                {mail.body_text ?? '(nessun contenuto)'}
              </pre>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
